<template>
    <QrWrapper>
        <div v-if="qrCode" class="max-w-7xl w-full space-y-4 pb-4">
            <QrHeader class="flex justify-between items-center">
                <div class="flex items-center">
                    <QrIcon icon="qrcode" class="mr-2"/>
                    <QrParagraph :text="trans('qrcode.order')" trans="qrcode.order"/>
                </div>
            </QrHeader>

            <div class="grid md:grid-cols-2 gap-4">
                <QrCard :header="trans('header.cart')" header-trans="header.cart">
                    <div class="space-y-2">
                        <div>
                            <QrParagraph :text="trans('label.product-name')" trans="label.product-name"/>
                            <QrParagraph class="font-semibold" :text="qrCode.product.name"/>
                        </div>


                        <div class="grid grid-cols-2">
                            <div>
                                <QrParagraph :text="trans('label.price-per-chip')" trans="label.price-per-chip"/>
                                <QrParagraph class="font-semibold" :text="qrCode.product.price_readable"/>
                            </div>

                            <div class="w-32">
                                <QrSelect
                                    v-model="form.quantity"
                                    :label="trans('label.quantity')"
                                    :options="quantityOptions"
                                />
                            </div>
                        </div>

                        <div>
                            <QrParagraph :text="trans('label.total')" trans="label.total"/>
                            <QrParagraph class="font-semibold" :text="totalPrice"/>

                            <QrParagraph :text="trans('label.vat')" trans="label.vat"/>
                            <QrParagraph :text="vat"/>
                        </div>

                        <Message
                            info
                            :header="trans('header.order_is_invoiced')"
                            header-trans="header.order_is_invoiced"
                            :body="trans('label.order_is_invoiced_desc')"
                            body-trans="label.order_is_invoiced_desc"
                        />
                    </div>
                </QrCard>

                <QrCard :header="trans('header.delivery')" header-trans="header.delivery">
                    <QrForm @submit.prevent="qrCodeStore.orderThroughAgency(form)" class="space-y-4">
                        <QrInput
                            v-model="form.name"
                            :errors="validationErrors.name"
                            :label="trans('input.name')"
                            trans="input.name"
                        />

                        <QrInput
                            v-model="form.phone"
                            :errors="validationErrors.phone"
                            :label="trans('input.phone')"
                            trans="homepage.title"
                        />

                        <QrInput
                            v-model="form.address"
                            :errors="validationErrors.address"
                            :label="trans('input.address')"
                            trans="input.address"
                        />

                        <div class="grid grid-cols-12 gap-4">
                            <div class="col-span-12 sm:col-span-8">
                                <QrInput
                                    v-model="form.city"
                                    :errors="validationErrors.city"
                                    :label="trans('input.city')"
                                    trans="input.city"
                                />
                            </div>

                            <div class="col-span-12 sm:col-span-4">
                                <QrInput
                                    v-model="form.zip_code"
                                    :errors="validationErrors.zip_code"
                                    :label="trans('input.zip_code')"
                                    trans="input.zip_code"
                                />
                            </div>
                        </div>

                        <QrInput
                            v-model="country"
                            :label="trans('input.country')"
                            disabled
                            trans="input.country"
                        />

                        <div class="flex mt-4">
                            <QrButton
                                positive
                                type="submit"
                                :text="trans('code.order')"
                                trans="code.order"
                                icon-right="cart-shopping-fast"
                                icon-type="light"
                                class="ml-auto"
                                :loading="loading"
                                :recently-succeeded="recentlySucceeded"
                                :recently-failed="recentlyFailed"
                            />
                        </div>
                    </QrForm>
                </QrCard>
            </div>
        </div>
    </QrWrapper>
</template>

<script setup>
import { useQrCodesStore} from "@/store/qrCodes";
import { trans } from "@/libs/helper.js";
import { computed, onBeforeMount, onMounted, ref } from "vue";
import { storeToRefs } from "pinia";
import {useRoute, useRouter} from "vue-router";
import QrIcon from "@/components/application-ui/elements/QrIcon.vue";
import QrButton from "@/components/application-ui/elements/QrButton.vue";
import QrInput from "@/components/application-ui/form/QrInput.vue";
import QrParagraph from "@/components/application-ui/content/QrParagraph.vue";
import QrHeader from "@/components/application-ui/content/QrHeader.vue";
import QrWrapper from "@/components/application-ui/panels/QrWrapper.vue";
import QrCard from "@/components/application-ui/panels/QrCard.vue";
import { useStatesStore } from "@/store/states.js";
import QrForm from "@/components/application-ui/content/QrForm.vue";
import QrSelect from "@/components/application-ui/form/QrSelect.vue";
import { useUserStore } from "@/store/user.js";
import Message from "@/components/application-ui/feedback/Message.vue";
import { usePriceFormatter } from "@/composables/priceFormatter.js";

const route = useRoute();
const router = useRouter();
const statesStore = useStatesStore();
const userStore = useUserStore();
const { loading, validationErrors, recentlySucceeded, recentlyFailed } = storeToRefs(statesStore);
const qrCodeStore = useQrCodesStore();
const { qrCode } = storeToRefs(qrCodeStore);
const { user } = storeToRefs(userStore);
const { formatPrice } = usePriceFormatter();

const country = ref('');

const form = ref({
    name: '',
    phone: '',
    address: '',
    zip_code: '',
    city: '',
    quantity: 1,
});

const quantityOptions = computed(() => {
    const options = [];
    for (let i = 1; i <= 10; i++) {
        options.push({ text: i, value: i });
    }

    return options;
});

const totalPrice = computed(() => {
    return formatPrice(qrCode.value.product.price * form.value.quantity, qrCode.value.product.currency);
});

const vat = computed(() => {
    return formatPrice((qrCode.value.product.price * form.value.quantity) * 0.25, qrCode.value.product.currency);
});

onBeforeMount(async () => {
    await qrCodeStore.fetchOne(route.params.qrCode)

    if (qrCode.value.order) {
        await router.push({name: 'qr-code', query: { order: "success" }}, qrCode.value.id);
    }
});

onMounted(() => {
    country.value = trans(`country.${user.value.agency.country}`);
    form.value.name = user.value.name;
});

</script>
